// LLM generated - Model Claude 4 Sonnet

import { useCallback } from 'react';
import type { AxiosError } from 'axios';

import { VoucherNotValidError } from '~/services/wallet/wallet.service.api';
import type { PublicBoost } from '~/domains/contest/domains/services/interfaces/propsService/api/boosts';
import type { PropPickProps } from '~/domains/contest/domains/common/types/propPickProps.type';
import useQuickPicksValidateBoost from '~/domains/contest/domains/quickPick/hooks/useValidateBoost';

import type { VoucherResponseDto } from '../types/voucher.types';
import { VoucherEntryType } from '../types/voucher.types';

import useQuickPicksValidateVoucher from './useValidateVoucher';

/**
 * Validation errors returned by the promotion validation hook
 */
export type PromotionValidationErrors = {
  boost?: string;
  voucher?: string;
};

/**
 * Parameters for the promotion validation hook
 */
export interface UsePromotionValidationParams {
  /**
   * Selected boost to validate (optional)
   */
  selectedBoost?: PublicBoost | null;
  /**
   * Selected voucher to validate (optional)
   */
  selectedVoucher?: VoucherResponseDto | null;
  /**
   * Default entry amount in dollars (can be overridden in validatePromotion call)
   */
  entryAmount?: number;
}

/**
 * Unified hook for validating both boosts and vouchers
 *
 * This hook consolidates boost and voucher validation logic that was previously
 * duplicated across multiple files. It provides a single, consistent interface
 * for validating promotions before submission or during entry creation.
 *
 * @param params - Validation parameters including selected promotions, picks, and entry amount
 * @returns Validation function that returns errors for both boost and voucher
 *
 * @example
 * ```typescript
 * const { validatePromotion } = useQuickPicksPromotionValidation({
 *   selectedBoost,
 *   selectedVoucher,
 *   picks: picks.value,
 *   entryAmount: selectedEntryAmount,
 * });
 *
 * const errors = await validatePromotion();
 * if (errors.boost) {
 *   // Handle boost validation error
 * }
 * if (errors.voucher) {
 *   // Handle voucher validation error
 * }
 * ```
 */
export function useQuickPicksPromotionValidation({
  selectedBoost,
  selectedVoucher,
  entryAmount,
}: UsePromotionValidationParams) {
  const validateBoost = useQuickPicksValidateBoost();
  const validateVoucher = useQuickPicksValidateVoucher();

  /**
   * Validate boost and/or voucher if selected
   * Returns an object with errors for each promotion type
   *
   * @param options - Validation options
   * @param options.picks - Array of picks to include in validation (required for boost validation)
   * @param options.overrideEntryAmount - Optional entry amount to use instead of the default from hook params
   * @param options.validateBoost - Whether to validate boost (default: true if selectedBoost is provided)
   * @param options.validateVoucher - Whether to validate voucher (default: true if selectedVoucher is provided)
   * @returns Object with boost and/or voucher error messages, or empty object if validation succeeds
   */
  const validatePromotion = useCallback(
    async (options?: {
      picks?: PropPickProps[];
      overrideEntryAmount?: number;
      validateBoost?: boolean;
      validateVoucher?: boolean;
    }): Promise<PromotionValidationErrors> => {
      const errors: PromotionValidationErrors = {};
      const amountToUse = options?.overrideEntryAmount ?? entryAmount ?? 0;
      const shouldValidateBoost = options?.validateBoost ?? !!selectedBoost?.id;
      const shouldValidateVoucher = options?.validateVoucher ?? !!selectedVoucher;

      // Validate boost if selected and should validate
      if (shouldValidateBoost && selectedBoost?.id && amountToUse > 0 && options?.picks) {
        try {
          await validateBoost.mutateAsync({
            boostId: selectedBoost.id,
            payload: {
              prop_ids: options.picks.map((pick) => pick.id),
              amount: amountToUse,
            },
          });
        } catch (error) {
          const axiosError = error as AxiosError<{ message?: string }>;
          errors.boost = axiosError?.response?.data?.message ?? 'Boost validation failed';
        }
      }

      // Validate voucher if selected and should validate
      if (shouldValidateVoucher && selectedVoucher && amountToUse > 0) {
        const voucherId = selectedVoucher.voucherId;
        try {
          await validateVoucher.mutateAsync({
            voucherId,
            payload: {
              voucherType: selectedVoucher.voucherType,
              amount: amountToUse,
              entryType: VoucherEntryType.QUICKPICKS,
              // Only include optional fields if they have values
              ...(selectedVoucher.restrictedContestId && {
                contestId: selectedVoucher.restrictedContestId,
              }),
              ...(selectedVoucher.league && { league: selectedVoucher.league }),
              ...(selectedVoucher.contestType && {
                contestType: selectedVoucher.contestType,
              }),
            },
          });
        } catch (error) {
          // A 200 with `isValid: false` is normalized into VoucherNotValidError,
          // carrying the backend reason directly.
          if (error instanceof VoucherNotValidError) {
            errors.voucher = error.reason;
          } else {
            // Handle different HTTP error response structures
            const axiosError = error as AxiosError<
              { message?: string } | { data?: { reason?: string } }
            >;
            const errorData = axiosError?.response?.data;
            errors.voucher =
              (errorData && 'message' in errorData ? errorData.message : undefined) ??
              (errorData && 'data' in errorData && errorData.data?.reason
                ? errorData.data.reason
                : undefined) ??
              'Voucher validation failed';
          }
        }
      }

      return errors;
    },
    [selectedBoost, selectedVoucher, entryAmount, validateBoost, validateVoucher]
  );

  return { validatePromotion };
}
